import React, { useEffect, useState, useCallback, useReducer } from "react";
import { Link } from "react-router-dom";
import { Search, Download } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { fmtDate, fmtMoney } from "../lib/format";

const STATUS_OPTIONS = [
    { value: "", label: "All statuses" },
    { value: "open", label: "Open" },
    { value: "awaiting_shipment", label: "Awaiting Shipment" },
    { value: "completed", label: "Completed" },
];

const TOLERANCE_OPTIONS = [
    { value: "", label: "Any tolerance" },
    { value: "ok", label: "OK" },
    { value: "overrun", label: "Overrun" },
    { value: "underrun", label: "Underrun" },
    { value: "mixed", label: "Mixed" },
];

const INITIAL_FILTERS = {
    status: "",
    customerId: "",
    etaFrom: "",
    etaTo: "",
    poFrom: "",
    poTo: "",
    blind: "",
    tolerance: "",
    producedLate: "",
};

function filtersReducer(state, action) {
    if (action.type === "set") return { ...state, [action.key]: action.value };
    if (action.type === "reset") return INITIAL_FILTERS;
    return state;
}

function filtersToQueryParams(f) {
    const p = {};
    if (f.status) p.status = f.status;
    if (f.customerId) p.customer_id = f.customerId;
    if (f.etaFrom) p.eta_from = f.etaFrom;
    if (f.etaTo) p.eta_to = f.etaTo;
    if (f.poFrom) p.po_from = f.poFrom;
    if (f.poTo) p.po_to = f.poTo;
    if (f.blind !== "") p.blind = f.blind === "true";
    if (f.tolerance) p.tolerance = f.tolerance;
    if (f.producedLate !== "") p.produced_late = f.producedLate === "true";
    return p;
}

function statusBadge(s) {
    if (s === "completed") return <span className="odr-badge odr-badge-completed">Completed</span>;
    if (s === "awaiting_shipment")
        return <span className="odr-badge odr-badge-awaiting">Awaiting</span>;
    return <span className="odr-badge odr-badge-open">Open</span>;
}

function toleranceBadge(t) {
    if (!t || t === "ok") return <span className="text-xs text-green-700">OK</span>;
    if (t === "overrun") return <span className="text-xs text-red-700 font-semibold">Overrun</span>;
    if (t === "underrun") return <span className="text-xs text-amber-700 font-semibold">Underrun</span>;
    return <span className="text-xs text-red-700 font-semibold">Mixed</span>;
}

function downloadCsv(filename, rows) {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [
        headers.join(","),
        ...rows.map((r) =>
            headers
                .map((h) => {
                    const v = r[h] ?? "";
                    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
                    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                })
                .join(","),
        ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function Reports() {
    const [customers, setCustomers] = useState([]);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");

    const [filters, dispatch] = useReducer(filtersReducer, INITIAL_FILTERS);
    const setFilter = (key) => (e) =>
        dispatch({ type: "set", key, value: e.target.value });

    useEffect(() => {
        api.get("/customers", { params: { active: true } }).then((r) => setCustomers(r.data));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get("/reports/orders", {
                params: filtersToQueryParams(filters),
            });
            setRows(data.rows);
            setSummary(data.summary);
        } catch (err) {
            console.error("Reports load failed", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = !search.trim()
        ? rows
        : rows.filter((r) =>
              [r.internal_number, r.customer_po, r.customer_name]
                  .filter(Boolean)
                  .some((v) => v.toLowerCase().includes(search.trim().toLowerCase())),
          );

    const exportRows = filtered.map((r) => ({
        order: r.internal_number,
        customer: r.customer_name,
        customer_po: r.customer_po,
        po_date: r.po_date,
        eta: r.eta,
        status: r.status,
        tolerance: r.production?.tolerance_status || "ok",
        produced_late: r.production?.produced_late ? "Yes" : "No",
        blind: r.blind_shipment ? "Yes" : "No",
        grand_total: r.grand_total ?? "",
    }));

    return (
        <div data-testid="reports-page">
            <PageHeader
                title="Reports"
                subtitle="Operational reporting across the entire order book"
            />

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        Status
                    </label>
                    <select
                        value={filters.status}
                        onChange={setFilter("status")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                        data-testid="rep-status"
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        Customer
                    </label>
                    <select
                        value={filters.customerId}
                        onChange={setFilter("customerId")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                        data-testid="rep-customer"
                    >
                        <option value="">All customers</option>
                        {customers.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        Tolerance
                    </label>
                    <select
                        value={filters.tolerance}
                        onChange={setFilter("tolerance")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                        data-testid="rep-tolerance"
                    >
                        {TOLERANCE_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        Produced Late
                    </label>
                    <select
                        value={filters.producedLate}
                        onChange={setFilter("producedLate")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                        data-testid="rep-produced-late"
                    >
                        <option value="">Any</option>
                        <option value="true">Late only</option>
                        <option value="false">On-time only</option>
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        ETA from
                    </label>
                    <input
                        type="date"
                        value={filters.etaFrom}
                        onChange={setFilter("etaFrom")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="rep-eta-from"
                    />
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        ETA to
                    </label>
                    <input
                        type="date"
                        value={filters.etaTo}
                        onChange={setFilter("etaTo")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="rep-eta-to"
                    />
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        PO from
                    </label>
                    <input
                        type="date"
                        value={filters.poFrom}
                        onChange={setFilter("poFrom")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="rep-po-from"
                    />
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        PO to
                    </label>
                    <input
                        type="date"
                        value={filters.poTo}
                        onChange={setFilter("poTo")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="rep-po-to"
                    />
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        Blind Shipment
                    </label>
                    <select
                        value={filters.blind}
                        onChange={setFilter("blind")}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                        data-testid="rep-blind"
                    >
                        <option value="">Any</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                        Search (Order #, PO, Customer)
                    </label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Order #, customer PO, or customer name…"
                            className="odr-input w-full rounded-sm border border-gray-300 pl-9 pr-3 text-sm"
                            data-testid="rep-search"
                        />
                    </div>
                </div>
            </div>

            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="rep-summary-count">
                        <div className="text-[11px] uppercase tracking-wider text-gray-500">Results</div>
                        <div className="text-2xl font-bold odr-mono">{summary.count}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="rep-summary-value">
                        <div className="text-[11px] uppercase tracking-wider text-gray-500">Total Value</div>
                        <div className="text-2xl font-bold odr-mono">{fmtMoney(summary.total_value)}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-sm p-3 md:col-span-2">
                        <div className="text-[11px] uppercase tracking-wider text-gray-500">By Status</div>
                        <div className="text-sm font-semibold text-gray-800 mt-1 flex flex-wrap gap-3">
                            {Object.entries(summary.by_status).map(([k, v]) => (
                                <span key={k}>
                                    {k}: <span className="odr-mono">{v}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="flex justify-end mb-2">
                <button
                    onClick={() => downloadCsv("orders_report.csv", exportRows)}
                    disabled={!filtered.length}
                    className="odr-btn rounded-sm border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 inline-flex items-center gap-1"
                    data-testid="rep-export-csv"
                >
                    <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
            </div>

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="odr-table" data-testid="reports-table">
                    <thead>
                        <tr>
                            <th>Order #</th>
                            <th>Customer</th>
                            <th>PO</th>
                            <th>PO Date</th>
                            <th>ETA</th>
                            <th>Status</th>
                            <th>Tolerance</th>
                            <th>Late</th>
                            <th className="text-right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={9} className="text-center text-gray-500 py-6">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center text-gray-500 py-6">
                                    No orders match the current filters.
                                </td>
                            </tr>
                        )}
                        {filtered.map((r) => (
                            <tr key={r.id} data-testid={`rep-row-${r.internal_number}`}>
                                <td className="odr-mono text-xs font-semibold">
                                    <Link to={`/orders/${r.id}`} className="text-red-700 hover:text-red-900">
                                        {r.internal_number}
                                    </Link>
                                </td>
                                <td className="text-xs">{r.customer_name}</td>
                                <td className="odr-mono text-xs">{r.customer_po}</td>
                                <td className="text-xs">{fmtDate(r.po_date)}</td>
                                <td className="text-xs">{fmtDate(r.eta)}</td>
                                <td>{statusBadge(r.status)}</td>
                                <td>{toleranceBadge(r.production?.tolerance_status)}</td>
                                <td>
                                    {r.production?.produced_late ? (
                                        <span className="text-xs text-red-700 font-semibold">Yes</span>
                                    ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                    )}
                                </td>
                                <td className="text-right odr-mono">
                                    {fmtMoney(r.grand_total || 0)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
